import type { LabelKind, LabelSizeId } from "../types";
import sparePart76x51 from "./templates/spare-part-76x51.zpl?raw";
import storageBin76x51 from "./templates/storage-bin-76x51.zpl?raw";
import passport76x51 from "./templates/passport-76x51.zpl?raw";
import sparePart100x50 from "./templates/spare-part-100x50.zpl?raw";
import storageBin100x50 from "./templates/storage-bin-100x50.zpl?raw";
import passport100x50 from "./templates/passport-100x50.zpl?raw";
import sparePart102x76 from "./templates/spare-part-102x76.zpl?raw";
import storageBin102x76 from "./templates/storage-bin-102x76.zpl?raw";
import passport102x76 from "./templates/passport-102x76.zpl?raw";
import sparePart56x25 from "./templates/spare-part-56x25.zpl?raw";
import storageBin56x25 from "./templates/storage-bin-56x25.zpl?raw";
import passport56x25 from "./templates/passport-56x25.zpl?raw";
import { ALL_LABEL_SIZES } from "./sizes";
import { SPARE_PART_PLACEHOLDERS } from "./spare-part";
import { STORAGE_BIN_PLACEHOLDERS } from "./storage-bin";
import { PASSPORT_PLACEHOLDERS } from "./passport";

export type LabelTemplateKey = `${LabelKind}:${LabelSizeId}`;

export interface ShippedLabelTemplate {
  kind: LabelKind;
  size: LabelSizeId;
  key: LabelTemplateKey;
  body: string;
  placeholders: readonly string[];
  label: string;
}

export function labelTemplateKey(kind: LabelKind, size: LabelSizeId): LabelTemplateKey {
  return `${kind}:${size}`;
}

/** Bundled ZPL templates shipped with the app — used when a company has no override. */
const UNORDERED_SHIPPED_LABEL_TEMPLATES: readonly ShippedLabelTemplate[] = [
  {
    kind: "spare-part",
    size: "76x51",
    key: "spare-part:76x51",
    body: sparePart76x51,
    placeholders: SPARE_PART_PLACEHOLDERS,
    label: "Spare part · 76 × 51 mm",
  },
  {
    kind: "spare-part", size: "100x50", key: "spare-part:100x50",
    body: sparePart100x50, placeholders: SPARE_PART_PLACEHOLDERS,
    label: "Spare part · 100 × 50 mm",
  },
  {
    kind: "storage-bin",
    size: "76x51",
    key: "storage-bin:76x51",
    body: storageBin76x51,
    placeholders: STORAGE_BIN_PLACEHOLDERS,
    label: "Storage bin · 76 × 51 mm",
  },
  {
    kind: "storage-bin", size: "100x50", key: "storage-bin:100x50",
    body: storageBin100x50, placeholders: STORAGE_BIN_PLACEHOLDERS,
    label: "Storage bin · 100 × 50 mm",
  },
  {
    kind: "passport",
    size: "76x51",
    key: "passport:76x51",
    body: passport76x51,
    placeholders: PASSPORT_PLACEHOLDERS,
    label: "Passport · 76 × 51 mm",
  },
  {
    kind: "passport", size: "100x50", key: "passport:100x50",
    body: passport100x50, placeholders: PASSPORT_PLACEHOLDERS,
    label: "Passport · 100 × 50 mm",
  },
  {
    kind: "spare-part", size: "102x76", key: "spare-part:102x76",
    body: sparePart102x76, placeholders: SPARE_PART_PLACEHOLDERS,
    label: "Spare part · 102 × 76 mm",
  },
  {
    kind: "storage-bin", size: "102x76", key: "storage-bin:102x76",
    body: storageBin102x76, placeholders: STORAGE_BIN_PLACEHOLDERS,
    label: "Storage bin · 102 × 76 mm",
  },
  {
    kind: "passport", size: "102x76", key: "passport:102x76",
    body: passport102x76, placeholders: PASSPORT_PLACEHOLDERS,
    label: "Passport · 102 × 76 mm",
  },
  { kind: "spare-part", size: "56x25", key: "spare-part:56x25", body: sparePart56x25, placeholders: SPARE_PART_PLACEHOLDERS, label: "Spare part · 56 × 25 mm" },
  { kind: "storage-bin", size: "56x25", key: "storage-bin:56x25", body: storageBin56x25, placeholders: STORAGE_BIN_PLACEHOLDERS, label: "Storage bin · 56 × 25 mm" },
  { kind: "passport", size: "56x25", key: "passport:56x25", body: passport56x25, placeholders: PASSPORT_PLACEHOLDERS, label: "Passport · 56 × 25 mm" },
] as const;

/** Ordered by ALL_LABEL_SIZES (stable within a size). */
export const SHIPPED_LABEL_TEMPLATES: readonly ShippedLabelTemplate[] = [...UNORDERED_SHIPPED_LABEL_TEMPLATES].sort(
  (a, b) => ALL_LABEL_SIZES.indexOf(a.size) - ALL_LABEL_SIZES.indexOf(b.size),
);

const SHIPPED_BY_KEY = new Map(
  SHIPPED_LABEL_TEMPLATES.map((t) => [t.key, t] as const),
);

/** Company defaults only offer sizes shipped for every label kind. */
export const COMMON_LABEL_TEMPLATE_SIZES: readonly LabelSizeId[] = ALL_LABEL_SIZES.filter(
  (size) => (["spare-part", "storage-bin", "passport"] as const).every(
    (kind) => SHIPPED_BY_KEY.has(labelTemplateKey(kind, size)),
  ),
);

export function getShippedLabelTemplateByKey(
  key: LabelTemplateKey,
): ShippedLabelTemplate | undefined {
  return SHIPPED_BY_KEY.get(key);
}

export function getShippedLabelTemplate(
  kind: LabelKind,
  size: LabelSizeId,
): ShippedLabelTemplate | undefined {
  return SHIPPED_BY_KEY.get(labelTemplateKey(kind, size));
}

export function getShippedZplBody(kind: LabelKind, size: LabelSizeId): string | undefined {
  return getShippedLabelTemplate(kind, size)?.body;
}

export function placeholdersForKind(kind: LabelKind): readonly string[] {
  const match = SHIPPED_LABEL_TEMPLATES.find((t) => t.kind === kind);
  return match?.placeholders ?? [];
}

export function isLabelTemplateKey(value: string): value is LabelTemplateKey {
  return SHIPPED_BY_KEY.has(value as LabelTemplateKey);
}
